import { createElement, type HTMLAttributes, type ReactElement, type Ref } from 'react';

import { classNames } from '@m1work/shared-utils';

import type { LayoutElement } from '../types/layoutElement';
import type { Responsive } from '../types/responsive';
import type { Space } from '../types/space';
import { getResponsiveClassNames } from '../utils/getResponsiveClassNames';
import { getResponsiveVars } from '../utils/getResponsiveVars';

import styles from './Grid.module.scss';

export type GridAlignment = 'start' | 'center' | 'end' | 'stretch';

export interface GridProps extends HTMLAttributes<HTMLElement> {
    /** HTML element to render. @default 'div' */
    as?: LayoutElement;
    /**
     * Columns: a number makes that many equal columns, a string is a `grid-template-columns` value.
     * @example columns={{ base: 1, md: 2, xl: 4 }}
     * @example columns={{ base: 1, xl: '1fr 420px' }}
     */
    columns?: Responsive<number | string>;
    /** Rows, as a `grid-template-rows` value. By default rows are created as needed. */
    rows?: Responsive<string>;
    /** Space between cells, a step of the spacing scale (4 = 16px). */
    gap?: Responsive<Space>;
    /** Space between rows only; overrides `gap` vertically. */
    rowGap?: Responsive<Space>;
    /** Space between columns only; overrides `gap` horizontally. */
    columnGap?: Responsive<Space>;
    /** Vertical alignment of items inside their cells (`align-items`). @default 'stretch' */
    align?: Responsive<GridAlignment>;
    /** Horizontal alignment of items inside their cells (`justify-items`). @default 'stretch' */
    justify?: Responsive<GridAlignment>;
    /** Render as `inline-grid` instead of `grid`. @default false */
    isInline?: boolean;
    ref?: Ref<HTMLElement>;
}

function toColumns(value: number | string): string {
    return typeof value === 'number' ? `repeat(${value}, minmax(0, 1fr))` : value;
}

function toRows(value: string): string {
    return value;
}

/**
 * Lays out its children in a grid of columns and rows that can change per breakpoint.
 * Layout only: no padding, background or borders. Use `Flex` for a single row or column.
 *
 * @example
 * <Grid columns={{ base: 1, md: 2, xl: 4 }} gap={4}>
 *     {products.map((product) => <ProductCard key={product.id} product={product} />)}
 * </Grid>
 */
export function Grid({
    as = 'div',
    columns,
    rows,
    gap,
    rowGap,
    columnGap,
    align,
    justify,
    isInline = false,
    className,
    style,
    ...props
}: GridProps): ReactElement {
    // createElement instead of JSX: its overload for HTML tags is generic over the element type, so one
    // `ref: Ref<HTMLElement>` fits every tag allowed in `as`.
    return createElement(as, {
        className: classNames(
            styles.grid,
            isInline && styles.inline,
            ...getResponsiveClassNames(styles, 'gap', gap),
            ...getResponsiveClassNames(styles, 'row-gap', rowGap),
            ...getResponsiveClassNames(styles, 'column-gap', columnGap),
            ...getResponsiveClassNames(styles, 'align', align),
            ...getResponsiveClassNames(styles, 'justify', justify),
            className,
        ),
        style: {
            ...getResponsiveVars('grid-columns', columns, toColumns),
            ...getResponsiveVars('grid-rows', rows, toRows),
            ...style,
        },
        ...props,
    });
}
